import { Location } from 'history';
import React, { ReactNode, useEffect, useReducer } from 'react';
import { useHistory } from 'react-router';

import { BaseRow } from '@sbu/components.datagrid';

import { defaultInitialDataGridPageState } from './constants';
import { DataGridPageContext } from './DataGridPageContext';
import { DataGridPageReducer, DataGridPageState } from './types';

export interface DataGridPageContextProviderProps<Row extends BaseRow> {
  children: ReactNode;
  reducer: DataGridPageReducer<Row>;
  initialStateOverrides?: Partial<DataGridPageState<Row>>;
}

export const DataGridPageContextProvider = <Row extends BaseRow>({
  children,
  reducer,
  initialStateOverrides,
}: DataGridPageContextProviderProps<Row>) => {
  const history = useHistory();

  const [state, dispatch] = useReducer(reducer, {
    // Cast required to enforce generics used in the provider
    ...(defaultInitialDataGridPageState as DataGridPageState<Row>),
    ...initialStateOverrides,
  });

  useEffect(() => {
    if (state.allowRedirect && state.redirectTo) {
      history.push(state.redirectTo);
    }
  }, [state.allowRedirect, history, state.redirectTo]);

  /**
   * Default Action Handlers
   * These simple handlers used by default for most pages. If you need to override anything for any reason,
   * you can utilize the exposed dispatch function and ignore the desired handler.
   */
  const onAdd = () => {
    dispatch({ type: 'ADD' });
  };

  const onEdit = (row: Row) => {
    dispatch({ type: 'EDIT', row });
  };

  const onExport = (row: Row) => {
    dispatch({ type: 'EXPORT', row });
  };

  const onClickRow = (row: Row) => {
    dispatch({ type: 'SELECT', row });
  };

  const onSelectMultiple = (rows?: Row[]) => {
    dispatch({ type: 'SELECT_MULTIPLE', rows });
  };

  const onDelete = (row: Row) => {
    dispatch({
      type: 'DELETE',
      row,
    });
  };

  const onConfirmDelete = () => {
    dispatch({ type: 'CONFIRM_DELETE' });
  };

  const onCloseAside = () => {
    dispatch({ type: 'CLOSE_ASIDE' });
  };

  const onSaveAside = (row: Row) => {
    dispatch({
      type: 'SAVE',
      row,
    });
  };

  const onCancelDelete = () => {
    dispatch({ type: 'CANCEL_DELETE' });
  };

  const onChangeAside = (dirty: boolean) => {
    dispatch({
      type: 'UPDATE_ASIDE_FORM',
      dirty,
    });
  };

  const onCancelDiscard = () => {
    dispatch({ type: 'CANCEL_DISCARD' });
  };

  const onConfirmDiscard = () => {
    if (state.nextLocation) {
      history.push(state.nextLocation);
    }
    dispatch({ type: 'CONFIRM_DISCARD' });
  };

  const onNavigate = (nextLocation: Location) => {
    dispatch({ type: 'NAVIGATE_AWAY', nextLocation });
  };

  const redirect = (redirectTo: string) => {
    dispatch({ type: 'REDIRECT', redirectTo });
  };

  return (
    <DataGridPageContext.Provider
      value={{
        ...state,
        onAdd,
        onEdit,
        onExport,
        onClickRow,
        onSelectMultiple,
        onDelete,
        onConfirmDelete,
        onCloseAside,
        onSaveAside,
        onCancelDelete,
        onChangeAside,
        onCancelDiscard,
        onConfirmDiscard,
        onNavigate,
        redirect,
        dispatch,
      }}
    >
      {children}
    </DataGridPageContext.Provider>
  );
};
